import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useCart } from "../context/CartContext";
import { FiShoppingBag, FiArrowLeft, FiPackage, FiTag, FiBookOpen, FiFacebook, FiTwitter, FiLink } from "react-icons/fi";
import ReviewSystem from "../components/ReviewSystem";
import toast from "react-hot-toast";
import SEO from "../components/SEO";

export default function BundleDetails() {
    const { id } = useParams();
    const [bundle, setBundle] = useState(null);
    const [loading, setLoading] = useState(true);
    const { addItem } = useCart();

    useEffect(() => {
        async function fetchBundle() {
            try {
                const snap = await getDoc(doc(db, "products", id));
                if (snap.exists()) {
                    setBundle({ id: snap.id, ...snap.data() });
                }
            } catch (e) {
                console.error("Error fetching bundle:", e);
            }
            setLoading(false);
        }
        fetchBundle();
    }, [id]);

    if (loading) return (
        <div style={{ padding: "120px 0", textAlign: "center", color: "#888", fontFamily: "'Inter', sans-serif" }}>
            Loading bundle details…
        </div>
    );

    if (!bundle || bundle.productType !== "bundle") return (
        <div style={{ padding: "100px 20px", textAlign: "center" }}>
            <h2>Bundle not found</h2>
            <Link to="/shop" style={{ color: "var(--accent)" }}>Back to Shop</Link>
        </div>
    );

    const items = bundle.bundleItems || [];
    const isOutOfStock = !bundle.isDigital && (bundle.stock ?? 0) <= 0;

    const seoData = {
        title: bundle.seoTitle || `${bundle.title} by ${bundle.author}`,
        description: bundle.seoDescription || (bundle.description
            ? bundle.description.replace(/<[^>]*>/g, "").substring(0, 160)
            : ""),
        keywords: bundle.seoKeywords || "",
        image: bundle.imageUrl,
        price: bundle.price,
        currency: "USD"
    };

    return (
        <div style={{ fontFamily: "'Inter', sans-serif", minHeight: "80vh" }}>
            <SEO pageId="shop" manualData={seoData} />
            {/* Hero */}
            <div style={{ background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)", padding: "80px 20px 60px", color: "#fff" }}>
                <div className="container">
                    <Link to="/shop" style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "#aaa", textDecoration: "none", marginBottom: 32, fontSize: 14 }}>
                        <FiArrowLeft size={14} /> Back to catalog
                    </Link>

                    <div style={{ display: "flex", gap: 60, flexWrap: "wrap", alignItems: "flex-start" }}>
                        {/* Bundle Cover */}
                        <div style={{ flex: "1 1 350px", position: "relative" }}>
                            <div style={{ position: "absolute", top: -12, left: -12, right: 12, bottom: 12, border: "2px solid var(--accent)", borderRadius: 16, zIndex: 0 }} />
                            <img
                                src={bundle.imageUrl}
                                alt={bundle.title}
                                style={{ width: "100%", borderRadius: 16, position: "relative", zIndex: 1, boxShadow: "0 30px 60px rgba(0,0,0,0.6)" }}
                            />
                        </div>

                        {/* Bundle Info */}
                        <div style={{ flex: "1 1 480px" }}>
                            <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                                <span style={{ background: "var(--accent)", color: "#fff", padding: "4px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>
                                    📦 Bundle
                                </span>
                                <span style={{ background: bundle.isDigital ? "#1a3a5c" : "#1a3a1a", color: bundle.isDigital ? "#7eb8f7" : "#7ed17e", padding: "4px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                                    {bundle.isDigital ? "Digital" : (bundle.bundleFormat || "Physical")}
                                </span>
                            </div>

                            <h1 style={{ fontSize: "2.8rem", lineHeight: 1.1, color: "#fff", margin: "0 0 12px" }}>{bundle.title}</h1>
                            <p style={{ fontSize: "1.1rem", color: "#aaa", marginBottom: 24 }}>By {bundle.author}</p>

                            {bundle.category && (
                                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#888", fontSize: 14, marginBottom: 24 }}>
                                    <FiTag size={14} style={{ color: "var(--accent)" }} />
                                    <span>{bundle.category}</span>
                                </div>
                            )}

                            {/* Bundle description */}
                            {bundle.description && (
                                <div style={{ marginBottom: 32 }}>
                                    {bundle.isPreorder && bundle.releaseDate && (
                                        <div style={{ background: "rgba(230, 81, 0, 0.1)", borderLeft: "4px solid #e65100", padding: "16px 20px", borderRadius: 8, marginBottom: 24, display: "flex", alignItems: "center", gap: 15 }}>
                                            <span style={{ fontSize: "1.4rem" }}>🕒</span>
                                            <div>
                                                <h4 style={{ margin: 0, color: "#ff9800", fontSize: "1rem", textTransform: "uppercase", letterSpacing: 0.5 }}>Pre-order Bundle</h4>
                                                <p style={{ margin: "4px 0 0", color: "#ccc", fontSize: "0.9rem" }}>
                                                    Releasing on <strong>{new Date(bundle.releaseDate + "T12:00:00").toLocaleDateString(undefined, { dateStyle: 'long' })}</strong>
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <div
                                        className="rich-text-content"
                                        dangerouslySetInnerHTML={{ __html: bundle.description }}
                                        style={{ color: "#ccc", lineHeight: 1.8, fontSize: "1rem" }}
                                    />
                                </div>
                            )}

                            {/* What's Included */}
                            <div style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "20px 24px", marginBottom: 32 }}>
                                <p style={{ fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: 1, color: "var(--accent)", marginBottom: 14 }}>
                                    <FiPackage size={14} style={{ marginRight: 8 }} />
                                    This bundle includes {items.length} book{items.length !== 1 ? "s" : ""}
                                </p>
                                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                    {items.map((item, i) => (
                                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--accent)", flexShrink: 0 }} />
                                            <span style={{ color: "#ddd", fontSize: 15 }}>{item.title}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Price + CTA */}
                            <div style={{ display: "flex", alignItems: "center", gap: 32 }}>
                                <div>
                                    {bundle.originalPrice && parseFloat(bundle.originalPrice) > parseFloat(bundle.price) && (
                                        <span style={{ display: "block", fontSize: 14, color: "#888", textDecoration: "line-through" }}>
                                            ${parseFloat(bundle.originalPrice).toFixed(2)}
                                        </span>
                                    )}
                                    <span style={{ fontSize: "2.5rem", fontWeight: 800, color: "var(--accent)", lineHeight: 1 }}>
                                        ${parseFloat(bundle.price).toFixed(2)}
                                    </span>
                                    {bundle.originalPrice && parseFloat(bundle.originalPrice) > parseFloat(bundle.price) && (
                                        <span style={{ display: "block", fontSize: 13, color: "#4caf50", fontWeight: 600, marginTop: 4 }}>
                                            Save ${(parseFloat(bundle.originalPrice) - parseFloat(bundle.price)).toFixed(2)}!
                                        </span>
                                    )}
                                </div>

                                <button
                                    onClick={() => {
                                        addItem({
                                            productId: bundle.id,
                                            id: bundle.id,
                                            cartId: `${bundle.id}_Bundle`,
                                            title: bundle.title,
                                            author: bundle.author,
                                            imageUrl: bundle.imageUrl,
                                            language: bundle.language || null,
                                            selectedFormat: "Bundle",
                                            price: bundle.price,
                                            isDigital: bundle.isDigital || false,
                                            isPreorder: bundle.isPreorder || false,
                                            releaseDate: bundle.releaseDate || null,
                                            bundleDelivery: bundle.isDigital ? "Digital" : (bundle.bundleFormat || "Physical"),
                                            bundleItems: bundle.bundleItems || [],
                                            epubStoragePath: "",
                                            epubFileName: "",
                                            metaContentId: bundle.metaContentId || null,
                                            isBundle: true
                                        });
                                        toast.success(bundle.isPreorder ? "Pre-order bundle added! 🎉" : "Bundle added to cart! 🎉");
                                    }}
                                    disabled={!bundle.isPreorder && isOutOfStock}
                                    style={{
                                        background: (!bundle.isPreorder && isOutOfStock) ? "#555" : (bundle.isPreorder ? "#e65100" : "var(--accent)"),
                                        color: "#fff",
                                        border: "none",
                                        padding: "18px 40px",
                                        borderRadius: 12,
                                        fontSize: 16,
                                        fontWeight: 700,
                                        cursor: (!bundle.isPreorder && isOutOfStock) ? "not-allowed" : "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        transition: "all 0.2s"
                                    }}
                                >
                                    <FiShoppingBag size={20} />
                                    {(!bundle.isPreorder && isOutOfStock) ? "Out of Stock" : bundle.isPreorder ? "Pre-order Bundle" : "Add Bundle to Cart"}
                                </button>
                            </div>

                            {!bundle.isDigital && bundle.stock > 0 && bundle.stock < 10 && (
                                <p style={{ marginTop: 12, color: "#f39c12", fontSize: 13, fontWeight: 600 }}>
                                    ⚠ Only {bundle.stock} bundles left!
                                </p>
                            )}

                            {/* Sharing buttons */}
                            <div style={{ marginTop: 48, paddingTop: 32, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                                <p style={{ fontSize: 13, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 1, marginBottom: 16 }}>Share this bundle</p>
                                <div style={{ display: "flex", gap: 12 }}>
                                    <button 
                                        onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, "_blank")}
                                        className="share-btn"
                                        title="Share on Facebook"
                                        style={{ background: "#1877f2", color: "#fff", border: "none", width: 40, height: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "transform 0.2s" }}
                                    >
                                        <FiFacebook size={18} />
                                    </button>
                                    <button 
                                        onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out this bundle: ${bundle.title} at LB Books!`)}&url=${encodeURIComponent(window.location.href)}`, "_blank")}
                                        className="share-btn"
                                        title="Share on Twitter"
                                        style={{ background: "#1da1f2", color: "#fff", border: "none", width: 40, height: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "transform 0.2s" }}
                                    >
                                        <FiTwitter size={18} />
                                    </button>
                                    <button 
                                        onClick={() => {
                                            navigator.clipboard.writeText(window.location.href);
                                            toast.success("Link copied to clipboard!");
                                        }}
                                        className="share-btn"
                                        title="Copy Link"
                                        style={{ background: "rgba(255,255,255,0.1)", color: "#fff", border: "none", width: 40, height: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "transform 0.2s" }}
                                    >
                                        <FiLink size={18} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Books in this Bundle */}
            {items.length > 0 && (
                <div style={{ padding: "80px 20px", background: "#fff" }}>
                    <div className="container">
                        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "2.2rem", color: "var(--primary)", textAlign: "center", marginBottom: 16 }}>
                            Books Included in This Bundle
                        </h2>
                        <p style={{ textAlign: "center", color: "#888", marginBottom: 56, fontSize: 15 }}>
                            Everything you get when you purchase <strong>{bundle.title}</strong>
                        </p>

                        <div style={{ display: "flex", flexDirection: "column", gap: 60 }}>
                            {items.map((item, index) => (
                                <div key={index} style={{
                                    display: "flex",
                                    gap: 48,
                                    flexWrap: "wrap",
                                    alignItems: "flex-start",
                                    flexDirection: index % 2 === 0 ? "row" : "row-reverse",
                                    paddingBottom: 60,
                                    borderBottom: index < items.length - 1 ? "1px solid #eee" : "none"
                                }}>
                                    {/* Book cover */}
                                    <div style={{ flex: "0 0 220px" }}>
                                        {item.imageUrl ? (
                                            <img
                                                src={item.imageUrl}
                                                alt={item.title}
                                                style={{ width: "100%", borderRadius: 12, boxShadow: "0 10px 30px rgba(0,0,0,0.12)" }}
                                            />
                                        ) : (
                                            <div style={{ width: "100%", paddingTop: "140%", background: "linear-gradient(135deg, #f0ede8, #e0ddd8)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                <FiBookOpen size={40} style={{ color: "#bbb" }} />
                                            </div>
                                        )}
                                    </div>

                                    {/* Book info */}
                                    <div style={{ flex: "1 1 400px" }}>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: 1 }}>
                                            Book {index + 1} of {items.length}
                                        </span>
                                        <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.8rem", color: "var(--primary)", margin: "10px 0 6px" }}>
                                            {item.title}
                                        </h3>
                                        {item.author && (
                                            <p style={{ color: "#888", fontSize: 14, marginBottom: 20 }}>By {item.author}</p>
                                        )}
                                        {item.description && (
                                            <div
                                                className="rich-text-content"
                                                dangerouslySetInnerHTML={{ __html: item.description }}
                                                style={{ color: "#555", lineHeight: 1.8, fontSize: 15 }}
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Reviews */}
            <div style={{ background: "#f9f9f9", padding: "0 20px 60px" }}>
                <div className="container">
                    <ReviewSystem productId={bundle.id} />
                </div>
            </div>
        </div>
    );
}
